import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Pencil, RotateCcw } from "lucide-react";
import { api } from "./api";

const Ctx = createContext({ overrides: {}, pages: [], owner: false, save: async () => {}, undo: async () => {} });

export const isOwnerSession = () =>
  sessionStorage.getItem("xp_admin_ok") === "1" && sessionStorage.getItem("xp_admin_role") === "owner";

export const ContentProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});
  const [pages, setPages] = useState([]);
  const [owner, setOwner] = useState(isOwnerSession());
  const loadPages = useCallback(() => {
    api.get("/pages").then((r) => setPages(r.data.pages || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/content").then((r) => setOverrides(r.data.overrides || {})).catch(() => {});
    loadPages();
    const sync = () => setOwner(isOwnerSession());
    window.addEventListener("focus", sync);
    window.addEventListener("xp-admin-changed", sync);
    window.addEventListener("xp-pages-changed", loadPages);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("xp-admin-changed", sync);
      window.removeEventListener("xp-pages-changed", loadPages);
    };
  }, [loadPages]);
  const save = useCallback(async (key, value, label) => {
    await api.post("/admin/content", { key, value, label });
    setOverrides((o) => ({ ...o, [key]: value }));
  }, []);
  const undo = useCallback(async (key) => {
    const r = await api.post("/admin/content/undo", { key });
    setOverrides((o) => ({ ...o, [key]: r.data.value }));
    return r.data.value;
  }, []);
  return <Ctx.Provider value={{ overrides, pages, owner, save, undo }}>{children}</Ctx.Provider>;
};

export const useContent = () => useContext(Ctx);
export const useContentValue = (key, fallback = "") => {
  const { overrides } = useContent();
  const v = overrides[key];
  return v == null || v === "" ? fallback : v;
};

const EditModal = ({ k, label, value, multiline, onClose }) => {
  const { save, undo } = useContent();
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const doSave = async () => {
    setBusy(true);
    try {
      await save(k, draft, label || k);
      toast.success("Saved — it's live now");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save — please try again");
    }
    setBusy(false);
  };
  const doUndo = async () => {
    try {
      await undo(k);
      toast.success("Previous version restored — it's live now");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to restore yet");
    }
  };
  return createPortal(
    <div data-testid="edit-modal" className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center px-5" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#121212] rounded-3xl border border-[#D4AF37]/40 p-5 text-left text-white">
        <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black uppercase">EDIT — {label || k}</p>
        {multiline ? (
          <textarea data-testid="edit-modal-input" value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 4000))}
            rows={5} autoFocus
            className="w-full mt-3 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
        ) : (
          <input data-testid="edit-modal-input" value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 4000))} autoFocus
            className="w-full mt-3 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
        )}
        <button data-testid="edit-modal-save" onClick={doSave} disabled={busy}
          className="w-full mt-3 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest disabled:opacity-50">
          {busy ? "SAVING…" : "SAVE — GOES LIVE NOW"}
        </button>
        <button data-testid="edit-modal-undo" onClick={doUndo}
          className="w-full mt-2 flex items-center justify-center gap-1.5 border border-white/20 text-white/70 font-bold rounded-full py-2.5 text-[10px] tracking-widest">
          <RotateCcw size={11} /> RESTORE PREVIOUS VERSION
        </button>
        <button data-testid="edit-modal-cancel" onClick={onClose}
          className="w-full mt-2 text-white/40 text-[10px] font-bold tracking-widest py-1">CANCEL</button>
      </div>
    </div>,
    document.body
  );
};

// Owner-only inline editor. Guests see plain content (or nothing when empty).
export const Editable = ({ k, label, fallback = "", as: Tag = "span", className = "", multiline = false }) => {
  const { overrides, owner } = useContent();
  const raw = overrides[k];
  const value = raw == null || raw === "" ? fallback : raw;
  const [open, setOpen] = useState(false);
  if (!owner) return value ? <Tag className={className}>{value}</Tag> : null;
  return (
    <>
      <Tag className={className}>
        {value || <span className="opacity-40 italic text-sm">empty — tap the pencil</span>}
        <button
          data-testid={`edit-${k}`}
          aria-label={`Edit ${label || k}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="ml-2 inline-flex align-middle items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37] text-black shrink-0"
        >
          <Pencil size={12} />
        </button>
      </Tag>
      {open && <EditModal k={k} label={label} value={value} multiline={multiline} onClose={() => setOpen(false)} />}
    </>
  );
};

// Owner-only editor for the dish extras Passport owns (POS owns everything else on a dish).
export const OwnerDishExtras = ({ dish, onSaved }) => {
  const { owner, undo } = useContent();
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState("");
  const [spice, setSpice] = useState(0);
  const [veg, setVeg] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!owner || !dish) return null;
  const start = () => {
    setTags((dish.tags || []).join(", "));
    setSpice(dish.spice ?? 0);
    setVeg(!!dish.vegetarian);
    setOpen(true);
  };
  const doSave = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/dish-extras/${dish.slug}`, {
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        spice: Number(spice) || 0,
        vegetarian: veg,
      });
      onSaved?.(r.data);
      toast.success("Dish extras saved — live now");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };
  const doUndo = async () => {
    try {
      const v = await undo(`dishx:${dish.slug}`);
      if (v) onSaved?.(v);
      toast.success("Previous extras restored");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to restore yet");
    }
  };
  return (
    <div className="px-4 pb-4">
      <button data-testid="owner-dish-extras-btn" onClick={start}
        className="w-full flex items-center justify-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] font-bold rounded-full py-2.5 text-[10px] tracking-widest">
        <Pencil size={11} /> EDIT DISH EXTRAS — TAGS, SPICE, VEG (POS owns the rest)
      </button>
      {open && createPortal(
        <div data-testid="dish-extras-modal" className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center px-5" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#121212] rounded-3xl border border-[#D4AF37]/40 p-5 text-left text-white">
            <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black uppercase">DISH EXTRAS — {dish.name}</p>
            <p className="text-[10px] text-white/45 mt-1">Name, price, photo, category and availability come from the POS push — change those in the POS.</p>
            <label className="block text-[10px] text-white/60 font-bold tracking-widest mt-3">DIETARY / SEARCH TAGS (comma separated)</label>
            <input data-testid="dish-extras-tags" value={tags} onChange={(e) => setTags(e.target.value)}
              className="w-full mt-1 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
            <label className="block text-[10px] text-white/60 font-bold tracking-widest mt-3">SPICE (0–5)</label>
            <input data-testid="dish-extras-spice" type="number" min="0" max="5" value={spice} onChange={(e) => setSpice(e.target.value)}
              className="w-24 mt-1 bg-[#1A1A1A] border border-white/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
            <label className="flex items-center gap-2 mt-3 text-xs font-bold">
              <input data-testid="dish-extras-veg" type="checkbox" checked={veg} onChange={(e) => setVeg(e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" />
              VEGETARIAN
            </label>
            <button data-testid="dish-extras-save" onClick={doSave} disabled={busy}
              className="w-full mt-4 bg-[#D4AF37] text-black font-bold rounded-full py-3 text-xs tracking-widest disabled:opacity-50">
              {busy ? "SAVING…" : "SAVE — GOES LIVE NOW"}
            </button>
            <button data-testid="dish-extras-undo" onClick={doUndo}
              className="w-full mt-2 flex items-center justify-center gap-1.5 border border-white/20 text-white/70 font-bold rounded-full py-2.5 text-[10px] tracking-widest">
              <RotateCcw size={11} /> RESTORE PREVIOUS VERSION
            </button>
            <button onClick={() => setOpen(false)} className="w-full mt-2 text-white/40 text-[10px] font-bold tracking-widest py-1">CANCEL</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
